import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
  priority?: boolean; // For above-the-fold images
  placeholder?: string; // Low quality placeholder
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  style,
  priority = false,
  placeholder,
  onLoad,
  onError,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Load immediately if priority
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (priority) return; // Skip lazy loading for priority images

    // Intersection Observer for lazy loading
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image comes into view
        threshold: 0.1,
      }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const imageStyle: React.CSSProperties = {
    width: width || '100%',
    height: height || 'auto',
    transition: 'opacity 0.3s ease',
    opacity: isLoaded ? 1 : 0,
    ...style,
  };

  const placeholderStyle: React.CSSProperties = {
    width: width || '100%',
    height: height || 'auto',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: isLoaded ? 'absolute' : 'static',
    top: 0,
    left: 0,
    opacity: isLoaded ? 0 : 1,
    transition: 'opacity 0.3s ease',
    backgroundImage: placeholder ? `url(${placeholder})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: placeholder ? 'blur(5px)' : undefined,
  };

  return (
    <Box
      ref={imgRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: width || '100%',
        height: height || 'auto',
      }}
      className={className}
    >
      {/* Placeholder */}
      <div style={placeholderStyle}>
        {!placeholder && !isLoaded && !hasError && (
          <div style={{ color: '#999', fontSize: '14px' }}>Loading...</div>
        )}
      </div>

      {/* Actual Image */}
      {(isInView || priority) && !hasError && (
        <img
          src={src}
          alt={alt}
          style={imageStyle}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'low'}
          decoding="async"
        />
      )}

      {/* Error fallback */}
      {hasError && (
        <div
          style={{
            ...placeholderStyle,
            opacity: 1,
            backgroundColor: '#f8f8f8',
            border: '1px solid #e0e0e0',
          }}
        >
          <div style={{ color: '#666', fontSize: '12px', textAlign: 'center' }}>
            Image failed to load
          </div>
        </div>
      )}
    </Box>
  );
};

export default OptimizedImage;